import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Geometry } from 'geojson'
import { z } from 'zod'
import { createApiClient } from '../../api/helpers.ts'
import { env } from '../../env.ts'
import { asQueryParam, encodeImpersonation, type Impersonation, useImpersonation } from '../impersonation.ts'
import { useSubscription } from '../service.ts'
import {
	IncomingLightningPriority,
	type LightningProtection,
	type LightningProtectionGroup,
	ParameterType,
	ProtectionType,
	type RequiredUserLists,
} from './types.ts'
import { API_GATEWAY_TIMEOUT, createTemporaryLightningProtectionVrn, lightningPriorityToRingMap } from './utils.ts'

const PAGE_SIZE = 100
export const LTZ_SUFFIX = '-ltz'

const LightningApi = createApiClient({ prefixUrl: env.API_LIGHTNING })

export type CreateLightningProtectionAreaRequest = {
	buffer: number
	clearTime: number
	label: string
	ltzMaxHorizon: number
	priority: LightningProtection['priority']
	cloud: boolean
	ltgDestListId?: string
	ltzDestListId?: string
}

type CreateLightningProtectionRequest = {
	areas: CreateLightningProtectionAreaRequest[]
	assetVrn: string
}

export const lightningProtectionsQuery = (impersonation: Impersonation | undefined, options: { enabled: boolean }) =>
	queryOptions({
		queryKey: ['LIGHTNING_PROTECTIONS', impersonation?.subscriptionId],
		refetchInterval: 1000 * 60,
		staleTime: 1000 * 60,
		enabled: options.enabled,
		queryFn: async () => {
			const retrievePages = async (page: number = 0, protections: IncomingRestLightning[] = []) => {
				const response = await LightningApi.get('api/v2/alert/areas', {
					searchParams: [
						['page', page],
						['size', PAGE_SIZE],
						['impersonation', encodeImpersonation(impersonation) ?? ''],
					],
				})
					.json<IncomingRestLightning[]>()
					.catch((error) => {
						console.error('Error fetching lightning alerts:', error)
						return []
					})

				const parsedProtections = Array.isArray(response)
					? response
							.map((item) => IncomingRestLightningSchema.safeParse(item))
							.filter((result) => result.success)
							.map((result) => result.data)
					: []

				const arr = [...protections, ...parsedProtections]

				if (response.length === PAGE_SIZE) return retrievePages(page + 1, arr)

				return arr
			}

			const data = await retrievePages()
			if (!data) return []

			const lightningProtections: LightningProtection[] = []

			data.forEach((alert: IncomingRestLightning) => {
				lightningProtections.push({
					type: ProtectionType.Lightning,
					parameterType: ParameterType.Realtime,
					priority: alert.priority,
					vrn: createTemporaryLightningProtectionVrn(alert.labelLocation, alert.priority),
					assetVrn: alert.labelLocation,
					geometry: alert.geom as Geometry,
					radius: alert.geomRadius,
					areaId: alert.id,
					createdTime: alert.createdTime,
				})
				if (alert.ltzMaxHorizonMinutes) {
					lightningProtections.push({
						type: ProtectionType.Lightning,
						parameterType: ParameterType.Forecast,
						priority: alert.priority,
						vrn: createTemporaryLightningProtectionVrn(`${alert.labelLocation}${LTZ_SUFFIX}`, alert.priority),
						assetVrn: alert.labelLocation,
						geometry: alert.geom as Geometry,
						radius: alert.geomRadius,
						areaId: alert.id,
						createdTime: alert.createdTime,
					})
				}
			})
			return lightningProtections
		},
	})

export const useLightningProtections = () =>
	useQuery(lightningProtectionsQuery(useImpersonation(), { enabled: useSubscription().data.features.includes('protections') }))

export const lightningProtectionQueryOptions = (areaIds: string[], impersonation: Impersonation | undefined) => ({
	queryKey: ['LIGHTNING_PROTECTION', ...areaIds, impersonation?.subscriptionId],
	refetchInterval: 1000 * 60,
	staleTime: 1000 * 60,
	queryFn: async (): Promise<LightningProtectionGroup> => {
		const requests = areaIds.map((areaId) =>
			LightningApi.get('api/v2/alert/areas/area', {
				searchParams: { areaId, ...asQueryParam(impersonation) },
			}).json<IncomingRestLightning>(),
		)
		const data = await Promise.all(requests)

		const config: LightningProtectionGroup = {
			type: ProtectionType.Lightning,
			assetVrn: data[0]?.labelLocation || '',
			rings: {},
		}

		data.forEach((protection) => {
			if (protection.ltzMaxHorizonMinutes) {
				config.ltz = {
					type: ProtectionType.Lightning,
					parameterType: ParameterType.Forecast,
					priority: protection.priority,
					vrn: createTemporaryLightningProtectionVrn(`${protection.labelLocation}${LTZ_SUFFIX}`, protection.priority),
					assetVrn: protection.labelLocation,
					geometry: protection.geom as Geometry,
					radius: protection.geomRadius,
					areaId: protection.id,
					createdTime: protection.createdTime,
					enabled: true,
					userLists: parseStringToUserLists(protection.metadata.ltzDestListId),
					horizonMinutes: protection.ltzMaxHorizonMinutes,
				}
			}
			const key = lightningPriorityToRingMap[protection.priority]
			config.rings[key] = {
				parameterType: ParameterType.Realtime,
				type: ProtectionType.Lightning,
				priority: protection.priority,
				vrn: createTemporaryLightningProtectionVrn(protection.labelLocation, protection.priority),
				assetVrn: protection.labelLocation,
				geometry: protection.geom as Geometry,
				radius: protection.geomRadius,
				areaId: protection.id,
				createdTime: protection.createdTime,
				clearTime: protection.clearTimeMinutes,
				includeCloudToCloud: protection.useCloud,
				enabled: true,
				userLists: parseStringToUserLists(protection.metadata.destListId),
			}
		})
		return config
	},
})

export const useLightningProtection = ({ areaIds }: { areaIds: string[] }) => {
	return useQuery(lightningProtectionQueryOptions(areaIds, useImpersonation()))
}

export const useCreateLightningProtection = () => {
	const impersonation = useImpersonation()
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (input: CreateLightningProtectionRequest) =>
			LightningApi.post('api/v2/alert/protect', {
				searchParams: asQueryParam(impersonation),
				json: input,
				timeout: API_GATEWAY_TIMEOUT,
			}),
		onSuccess: async () => await queryClient.invalidateQueries({ queryKey: ['LIGHTNING_PROTECTIONS', impersonation?.subscriptionId] }),
		// Replace this with whatever error handling UX decides
		onError: (error) => console.error('Error creating lightning protection:', error),
	})
}

export const useDeleteLightningProtection = () => {
	const impersonation = useImpersonation()
	const queryClient = useQueryClient()
	return useMutation({
		// Technically assetVrn but we use vrn for type consistency
		mutationFn: (input: { vrn: string }) =>
			LightningApi.delete('api/v2/alert/protect', {
				searchParams: { assetVrn: input.vrn, ...asQueryParam(impersonation) },
				timeout: API_GATEWAY_TIMEOUT,
			}),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['LIGHTNING_PROTECTIONS', impersonation?.subscriptionId] }),
		// Replace this with whatever error handling UX decides
		onError: (error) => console.error('Error deleting lightning protection:', error),
	})
}

const IncomingRestLightningSchema = z.object({
	id: z.string(),
	labelLocation: z.string().startsWith('vrn'),
	createdTime: z.string().startsWith('20'),
	geom: z.object({
		type: z.any(),
		coordinates: z.array(z.any()),
	}),
	priority: z.enum(IncomingLightningPriority),
	useCloud: z.boolean(),
	clearTimeMinutes: z.number().min(0),
	geomRadius: z.number().min(0),
	ltzMaxHorizonMinutes: z.number().min(0),
	metadata: z.object({
		destListId: z.string().startsWith('vrn').optional(),
		ltzDestListId: z.string().startsWith('vrn').optional(),
	}),
})
type IncomingRestLightning = z.infer<typeof IncomingRestLightningSchema>

const parseStringToUserLists = (input: string | undefined): RequiredUserLists<'immediate'> => {
	return { immediate: input ? input.split(',').map((ul) => ({ vrn: ul })) : [] }
}
